import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  FormControl,
  FormLabel,
  Input,
  Link,
} from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import SeparatorWithText from 'components/elements/SeparatorWithText';
import SSOButton from 'components/elements/SSOButton';
import { WarningIcon } from 'components/icons/WarningIcon';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import ForgotPassword from 'components/modals/ForgotPassword';
import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { trackChangePassword, trackPageView } from 'utils/analytics';
import {
  ChangePasswordClickActions,
  LoginPageViewSections,
  LoginPageViewSubSections,
} from 'utils/analytics/constants';
import { trackLoginWithSSO } from 'utils/analytics/ssoAuth';
import { SSO_PROVIDERS } from 'utils/constants/ssoProviders';

const FORGOT_PASSWORD_BUTTON_TEXT = 'Forgot password';

const bodyStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
};

const containerStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  paddingTop: 'padding_48',
  paddingBottom: 'padding_48',
  paddingX: 'padding_16',
  margin: 'auto',
};

const titleStyle = {
  paddingBottom: 'padding_24',
};

const linkToDemoStyle = {
  marginTop: 'padding_48',
  '.chakra-link': {
    textDecoration: 'none',
    _hover: {
      textDecoration: 'underline',
    },
  },
  flexWrap: 'wrap',
  maxWidth: '360px',
};

const shortcutText = {
  color: Colors.ForestGreen900,
  letterSpacing: '-0.01em',
  textDecoration: 'none',
};

const demoContent = {
  gap: 'padding_8',
};

const formContainer = {
  gap: 'padding_8',
  width: '100%',
  maxWidth: '360px',
};

const demoLink = {
  width: '100%',
};

const forgotPasswordLink = { color: Colors.ForestGreen900, cursor: 'pointer' };

const formControl = { width: '100%' };

const alertStyle = {
  padding: '4px',
  marginTop: '8px',
  backgroundColor: '#fef4ee',
  borderRadius: '4px',
  width: 'fit-content',
};

const alertDescription = { color: Colors.Tangerine700 };

const alertContent = { gap: '6px' };

const alertText = {
  fontSize: '12px',
  fontWeight: '600',
  letterSpacing: '-2%',
};

const submitButtonStyle = { mt: 'padding_16' };

const ssoOptionsStyle = {
  gap: 'padding_8',
  justifyContent: 'center',
};

const dividerStyle = {
  mt: 'padding_24',
};

const Login = () => {
  const { renderCustomToast: toast } = useToast();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<boolean>(false);
  const { login, loginWithMicrosoftSSO } = useAuth();
  const [passwordModal, setPasswordModal] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setErrors(false);
      // form validation
      if (!username || !password) {
        setErrors(true);
        toast({
          title: 'Error',
          description: 'Please enter a username and password',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });

        return;
      }
      setIsLoading(true);
      await login(username, password);
      setIsLoading(false);
    },
    [username, password, login, toast],
  );

  const handleLogInWithSSO = useCallback(
    (clickText: string) => {
      trackLoginWithSSO({
        clickAction: 'start',
        provider: SSO_PROVIDERS.MICROSOFT,
        sectionName: LoginPageViewSections.LOGIN,
        clickText,
      });

      loginWithMicrosoftSSO();
    },
    [loginWithMicrosoftSSO],
  );

  const inputStyles = {
    borderColor: `${errors ? '#FF6813' : 'brand-grey.light'}`,
    height: '44px',
  };

  useEffect(() => {
    trackPageView({
      sectionName: LoginPageViewSections.LOGIN,
      subSectionName: LoginPageViewSubSections.LOGIN,
    });
  }, []);

  return (
    <BasePageWithTopNav sx={bodyStyle}>
      <FlexRow sx={containerStyle}>
        <form onSubmit={handleSubmit}>
          <FlexColumn sx={formContainer}>
            <Text.H3 sx={titleStyle}>Account Sign In</Text.H3>
            <FormControl sx={formControl}>
              <FormLabel htmlFor="email-input">Email</FormLabel>
              <Input
                id="email-input"
                onChange={(e) => setUsername(e.target.value)}
                value={username}
                sx={inputStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password-input">Password</FormLabel>
              <Input
                id="password-input"
                type="password"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                sx={inputStyles}
              />
            </FormControl>
            <FlexRow
              onClick={() => {
                setPasswordModal(!passwordModal);
                trackChangePassword({
                  clickAction: ChangePasswordClickActions.START,
                  clickText: FORGOT_PASSWORD_BUTTON_TEXT,
                });
              }}
            >
              <Text sx={forgotPasswordLink}>{FORGOT_PASSWORD_BUTTON_TEXT}</Text>
            </FlexRow>
          </FlexColumn>

          <Alert status="error" display={errors ? 'flex' : 'none'} sx={alertStyle}>
            <AlertDescription sx={alertDescription}>
              <FlexRow sx={alertContent}>
                <WarningIcon />
                <Text sx={alertText}>Email and password are required</Text>
              </FlexRow>
            </AlertDescription>
          </Alert>
          <Button
            endAdornment={<Icon name={Icon.Name.ChevronRight} />}
            isDisabled={isLoading}
            sx={submitButtonStyle}
            type="submit"
          >
            Sign In
          </Button>

          <SeparatorWithText sx={dividerStyle} text="Or sign in with:" />

          <FlexColumn sx={ssoOptionsStyle}>
            <SSOButton
              provider="microsoft"
              label="Microsoft"
              onClick={() => handleLogInWithSSO('Microsoft')}
            />
          </FlexColumn>

          <FlexRow sx={linkToDemoStyle}>
            <FlexColumn sx={demoContent}>
              <Link
                href="https://www.mercator.ai/pricing?utm_source=app.mercator.ai&utm_medium=referral&utm_campaign=login-page"
                target="_blank"
                sx={demoLink}
              >
                <Text sx={shortcutText}>Not a Mercator.ai subscriber?</Text>
              </Link>
              <Text.Body>
                Discover how Mercator.ai can identify the most viable projects for you,
                and who’s involved, early.
              </Text.Body>
            </FlexColumn>
          </FlexRow>
          <FlexRow />
        </form>
        <ForgotPassword
          IsPasswordModalOpen={passwordModal}
          setIsPasswordModalOpen={setPasswordModal}
          onSaveClick={(clickText) => {
            trackChangePassword({
              clickAction: ChangePasswordClickActions.REQUEST,
              clickText,
            });
          }}
          onCancelClick={() => setPasswordModal(!passwordModal)}
        />
      </FlexRow>
    </BasePageWithTopNav>
  );
};

export default Login;
